import React, { useEffect, useState } from "react";
import { PageHeader, Card, Badge, Button, Input, Select, Textarea, Spinner, EmptyState } from "@/components/ui";
import { endpoints } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Building2, Plus, X, Calculator, Trash2, ExternalLink, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CompanyDrawer, CATEGORY_LABELS } from "@/components/CompanyDrawer";

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [enrichingId, setEnrichingId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: "", sector: "", country: "Italy", revenue: "", ebitda: "", employees: "", description: "" });
  const navigate = useNavigate();

  const [q, setQ] = useState("");
  const [etype, setEtype] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const r = await endpoints.companies({ q, entity_type: etype, limit: 1000 });
      setCompanies(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, etype]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    await endpoints.createCompany({
      ...form,
      revenue: Number(form.revenue) || 0,
      ebitda: Number(form.ebitda) || 0,
      employees: Number(form.employees) || 0,
    });
    setShowAdd(false);
    setForm({ name: "", sector: "", country: "Italy", revenue: "", ebitda: "", employees: "", description: "" });
    load();
  };

  const handleValuate = async (company) => {
    setGeneratingId(company.id);
    try {
      const r = await endpoints.generateValuation(company.id);
      navigate(`/valuations?vid=${r.data.id}`);
    } catch (e) {
      alert("Valuation generation failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this company?")) return;
    await endpoints.deleteCompany(id);
    load();
  };

  const handleEnrich = async (id) => {
    setEnrichingId(id);
    try {
      await endpoints.enrichCompany(id);
      await load();
    } catch (e) {
      alert("Live data unavailable: " + (e?.response?.data?.detail || e.message));
    } finally { setEnrichingId(null); }
  };

  return (
    <div className="p-4 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="CLARA Core"
        title="Target Universe"
        description="Portfolio companies, prospects and acquisition targets — ready for institutional-grade valuation."
        testid="companies-page"
        actions={
          <Button onClick={() => setShowAdd(true)} data-testid="add-company-btn">
            <Plus size={14} /> Add Company
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="companies-filters">
        <Input placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="companies-search" className="sm:max-w-xs" />
        <Select value={etype} onChange={(e) => setEtype(e.target.value)} data-testid="companies-type-filter" className="sm:max-w-[180px]">
          <option value="">All entities</option>
          <option value="target">Targets</option>
          <option value="investor">Investors</option>
        </Select>
      </div>

      {loading ? <div className="text-white/50"><Spinner /> Loading companies…</div> :
       !companies.length ? <EmptyState icon={Building2} title="No companies yet" description="Add your first target company to begin valuation." action={<Button onClick={() => setShowAdd(true)}>Add Company</Button>} /> :
      (
        <div className="clara-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="companies-table">
            <thead>
              <tr className="border-b border-white/5 text-left">
                <th className="px-4 py-3 overline font-normal">Company</th>
                <th className="px-4 py-3 overline font-normal">Sector</th>
                <th className="px-4 py-3 overline font-normal hidden lg:table-cell">Category</th>
                <th className="px-4 py-3 overline font-normal text-right">Revenue</th>
                <th className="px-4 py-3 overline font-normal text-right">EBITDA</th>
                <th className="px-4 py-3 overline font-normal text-right">Margin</th>
                <th className="px-4 py-3 overline font-normal hidden md:table-cell">Country</th>
                <th className="px-4 py-3 overline font-normal hidden md:table-cell">Ticker</th>
                <th className="px-4 py-3 overline font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} onClick={() => setSelected(c)} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] cursor-pointer" data-testid={`company-row-${c.id}`}>
                  <td className="px-4 py-3.5">
                    <div className="font-medium flex items-center gap-2">
                      {c.name}
                      {c.decision_makers?.length > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-gold" title="Decision makers found" data-testid={`dm-count-${c.id}`}><Users size={10} /> {c.decision_makers.length}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/40 truncate max-w-xs">{c.description}</div>
                  </td>
                  <td className="px-4 py-3.5 text-white/70 text-[12px]">{c.sector}</td>
                  <td className="px-4 py-3.5 hidden lg:table-cell">
                    {CATEGORY_LABELS[c.category] ? <Badge tone={CATEGORY_LABELS[c.category].tone}>{CATEGORY_LABELS[c.category].label}</Badge> : <span className="text-white/20 text-[11px]">—</span>}
                  </td>
                  <td className="px-4 py-3.5 text-right font-mono text-[12px]">{fmt.money(c.revenue)}</td>
                  <td className="px-4 py-3.5 text-right font-mono text-[12px]">{fmt.money(c.ebitda)}</td>
                  <td className="px-4 py-3.5 text-right">
                    <span className={c.ebitda_margin > 15 ? "text-gold" : c.ebitda_margin > 8 ? "text-white" : "text-white/40"}>
                      {fmt.pct(c.ebitda_margin)}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-white/50 text-[12px] hidden md:table-cell">{c.country}</td>
                  <td className="px-4 py-3.5 text-[11px] font-mono hidden md:table-cell">
                    {c.ticker ? <span className="text-gold">{c.ticker}</span> : <span className="text-white/25">Private</span>}
                  </td>
                  <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {c.ticker && (
                        <Button size="sm" variant="outline" onClick={() => handleEnrich(c.id)} disabled={enrichingId === c.id} data-testid={`enrich-btn-${c.id}`}>
                          {enrichingId === c.id ? <Spinner size={12} /> : <><ExternalLink size={12} /> Live</>}
                        </Button>
                      )}
                      <Button size="sm" variant="primary" onClick={() => handleValuate(c)} disabled={generatingId === c.id} data-testid={`valuate-btn-${c.id}`}>
                        {generatingId === c.id ? <><Spinner size={12} /> Generating…</> : <><Calculator size={12} /> Valuate</>}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(c.id)} data-testid={`delete-company-${c.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <CompanyDrawer
          company={selected}
          onClose={() => setSelected(null)}
          onUpdated={(next) => { setSelected(next); setCompanies((prev) => prev.map((x) => (x.id === next.id ? next : x))); }}
        />
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowAdd(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={handleCreate} className="clara-card w-full max-w-lg p-6 md:p-8" data-testid="add-company-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline">New Target</div>
                <h2 className="font-display text-2xl mt-1">Add Company</h2>
              </div>
              <button type="button" onClick={() => setShowAdd(false)} className="text-white/40 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="overline mb-1.5 block">Name</label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-input-name" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Sector</label>
                <Input required value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} data-testid="company-input-sector" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Country</label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="company-input-country" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Revenue (€)</label>
                <Input type="number" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} data-testid="company-input-revenue" />
              </div>
              <div>
                <label className="overline mb-1.5 block">EBITDA (€)</label>
                <Input type="number" value={form.ebitda} onChange={(e) => setForm({ ...form, ebitda: e.target.value })} data-testid="company-input-ebitda" />
              </div>
              <div>
                <label className="overline mb-1.5 block">Employees</label>
                <Input type="number" value={form.employees} onChange={(e) => setForm({ ...form, employees: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="overline mb-1.5 block">Description</label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button type="submit" data-testid="submit-company">Save Company</Button>
              <Button type="button" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
